<template>
  <Transition name="fade">
    <DrawerDialog
      v-if="isOpen"
      title="Copy Order"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="true"
      md
      @closeEvent="closeModal"
      @cancelEvent="closeModal"
      @saveEvent="submitCopyOrder"
      :saveButtonProps="{ disabled: false }"
      :saveButtonDisabled="isSaveDisabled || !canSubmit"
    >
      <template #modal-content>
        <div class="flex flex-col gap-3">
          <!-- Order Admin -->
          <div class="border-b pb-3 mb-1">
            <h2 class="text-lg font-semibold mb-2">Order Admin</h2>
            <div
              v-if="isOriginalAdminActive && originalAdmin"
              class="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            >
              <span class="font-medium">{{ originalAdmin.name }}</span>
              <span class="text-gray-500"> will be auto-assigned to the copied order.</span>
            </div>
            <div v-else>
              <div
                class="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md px-3 py-2 mb-2"
              >
                The original Order Admin is no longer active or available. Please assign an Order
                Admin manually before continuing.
              </div>
              <Select
                v-model="selectedOrderAdmin"
                :options="orderAdmins"
                label="name"
                track-by="id"
                placeholder="Select Order Admin"
                append-to-body
              />
            </div>
          </div>

          <!--
            AB#21667 (Zendesk #9310). Copying a remake used to produce another remake in
            silence: the API replicates the source order and never reset its type, so a
            reorder arrived carrying the "R" tag and counting against the remake rate. The
            question is asked here because this is the only moment anyone knows the answer.
            Shown for a remake and nothing else — most copies are of ordinary orders.
          -->
          <div v-if="sourceIsRemake" class="border-b pb-3 mb-2" data-test="copy-classification">
            <h2 class="text-lg font-semibold">Is this a remake or a reorder?</h2>
            <p class="text-sm text-ink-soft mb-2">
              {{ order.order_no }} is a remake. A reorder of it is not a new failure and does not
              count against the remake rate.
            </p>
            <label class="flex items-start gap-2 cursor-pointer mb-1">
              <input
                type="radio"
                value="remake"
                v-model="classification"
                class="mt-1"
                data-test="copy-classification-remake"
              />
              <span class="text-sm">
                <span class="font-medium">Remake</span>
                — something went wrong again. Counts toward the remake rate.
              </span>
            </label>
            <label class="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                value="reorder"
                v-model="classification"
                class="mt-1"
                data-test="copy-classification-reorder"
              />
              <span class="text-sm">
                <span class="font-medium">Reorder</span>
                — the customer wants more of the remade order. Nothing failed.
              </span>
            </label>

            <!--
              The questionnaire is a separate step rather than inlined, for the same reason
              ChangeOrderTypeModal keeps it separate: it is obvious that a failure is being
              reported, not a field being filled in.
            -->
            <div v-if="classification === 'remake'" class="mt-3 flex items-center gap-3">
              <button
                type="button"
                class="whitespace-nowrap rounded-md border border-[var(--accent)] px-2 py-1.5 text-xs font-medium text-[var(--accent)]"
                data-test="copy-classification-questionnaire"
                @click="showQuestionnaire = true"
              >
                {{ failureRecordAnswers ? 'Review answers' : 'Open questionnaire' }}
              </button>
              <span v-if="!failureRecordAnswers" class="text-sm text-red-600">
                The remake intake questionnaire is required before copying.
              </span>
            </div>
          </div>

          <!--
            AB#22228 (Zendesk #9545). Shown after Save when a selected product's roster has a
            size the product no longer offers — the copy waits for a replacement for each.
          -->
          <ResolveObsoleteSizes
            v-if="sizeReports.length"
            v-model="sizeChoices"
            :reports="sizeReports"
            class="border-b pb-3 mb-2"
          />

          <!-- Header -->
          <div class="flex items-center justify-between border-b pb-2 mb-2">
            <h2 class="text-lg font-semibold">Select Products to Copy</h2>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="selectAllItems" @change="toggleSelectAllItems" />
              <span class="text-sm font-medium">Select All Items</span>
            </label>
          </div>

          <!-- Items -->
          <div v-for="(item, itemIndex) in order.items" :key="item.id" class="w-full">
            <Accordion :default-open="true">
              <template #header>
                <div class="flex items-center gap-3 w-full">
                  <input
                    type="checkbox"
                    v-model="itemSelections[item.id].selectAll"
                    class="w-4 h-4 text-primary"
                    @change="toggleItemSelectAll(item)"
                    @click="(e) => e.stopPropagation()"
                  />
                  <!--
                    `item_no` is the public-facing item number — the order
                    number for a solo item, `<order_no>-1`, `-2` for a split one
                    (see order_items_table.md). This printed `item.id`, the
                    database key, which is a different number that looks like
                    the same kind of number. Position is the fallback rather
                    than the id: it is honest, it is what the reader can see on
                    screen anyway, and it can never be mistaken for a number
                    that identifies something else (AB#21223).
                  -->
                  <span class="font-medium" data-test="copy-order-item-label">
                    {{ item.item_no ? `Item ${item.item_no}` : `Item ${itemIndex + 1}` }}
                  </span>
                </div>
              </template>

              <template #content>
                <div class="grid grid-cols-3 gap-4 mt-2">
                  <div
                    v-for="fp in item.factory_products"
                    :key="fp.id"
                    role="checkbox"
                    tabindex="0"
                    :aria-checked="itemSelections[item.id].selectedProducts.includes(fp.id)"
                    @click="toggleProductSelection(item.id, fp.id)"
                    @keydown="triggerClickOnEnterOrSpace"
                    class="relative cursor-pointer border rounded-lg p-2 flex flex-col items-center transition-all duration-150"
                    :class="{
                      'border-primary bg-primary/10': itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                      'border-line hover:border-primary/50': !itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                    }"
                  >
                    <!-- Checkbox (optional small corner one) -->
                    <input
                      type="checkbox"
                      :checked="itemSelections[item.id].selectedProducts.includes(fp.id)"
                      class="absolute top-2 left-2 w-4 h-4 text-primary pointer-events-none"
                    />

                    <!-- Product Info -->
                    <div class="grid grid-cols-2 gap-2 w-full">
                      <div class="flex flex-col items-center">
                        <img
                          v-if="
                            fp.front_image &&
                            !copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'f')]
                          "
                          :src="storage_url + '/' + fp.front_image"
                          alt="front"
                          class="size-[100px] object-cover rounded-md border border-line"
                          @error="
                            copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'f')] = true
                          "
                        />
                        <div
                          v-else
                          class="size-[100px] shrink-0 rounded-md border border-line bg-surface"
                          aria-hidden="true"
                        />
                        <span class="text-xs mt-1 text-ink-soft">Front</span>
                      </div>
                      <div class="flex flex-col items-center">
                        <img
                          v-if="
                            fp.back_image &&
                            !copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'b')]
                          "
                          :src="storage_url + '/' + fp.back_image"
                          alt="back"
                          class="size-[100px] object-cover rounded-md border border-line"
                          @error="
                            copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'b')] = true
                          "
                        />
                        <div
                          v-else
                          class="size-[100px] shrink-0 rounded-md border border-line bg-surface"
                          aria-hidden="true"
                        />
                        <span class="text-xs mt-1 text-ink-soft">Back</span>
                      </div>
                    </div>

                    <p class="text-sm font-medium mt-2 text-center">
                      <!--
                        The SKU number when the product has no name, not the
                        row's database id: a SKU is something the reader can
                        look up, and `#${fp.id}` was a number nobody can act on
                        dressed as one they can (AB#21223).
                      -->
                      {{
                        (fp.new_product_name ? fp.new_product_name : fp.product_name) ||
                        (fp.sku_number ? `SKU ${fp.sku_number}` : 'Unnamed product')
                      }}
                    </p>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
        </div>
      </template>
    </DrawerDialog>
  </Transition>

  <RemakeIntakeQuestionnaireModal
    :show="showQuestionnaire"
    :order-no="order.order_no"
    :original-order-no="null"
    :suggested-record-type="classification"
    :order-type-symbol="order.order_type_symbol ?? null"
    @submit="onQuestionnaireSubmit"
    @cancel="showQuestionnaire = false"
  />
</template>

<script setup lang="ts">
import RemakeIntakeQuestionnaireModal from '@/components/Dashboard/Modals/RemakeIntakeQuestionnaireModal.vue';
import Accordion from '@/components/Shared/Accordion.vue';
import ResolveObsoleteSizes from '@/components/Shared/ResolveObsoleteSizes.vue';
import DrawerDialog from '@/components/Shared/DrawerDialog.vue';
import Select from '@/components/Shared/Select.vue';
import { showAlert, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import {
  CopyOrderResponseData,
  OrderAdminAssignment,
  OrderAdminUser,
  OrderDetail,
  OrderDetailItem,
} from '@/models';
import { useMainStore } from '@/store';
import { triggerClickOnEnterOrSpace } from '@/utils/a11y';
import {
  type ObsoleteSizeReport,
  type SizeReplacementChoices,
  allSizesResolved,
  buildSizeReplacements,
  reportsFromObsoleteSizeError,
  reportsFromResponse,
} from '@/utils/obsoleteSizes';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { ORDER_ADMIN_ROLES } from '@/utils/orderAdminRoles';

const storage_url = import.meta.env.VITE_APP_FILE_URL;
const props = defineProps<{ order: OrderDetail }>();
const isSubmitting = ref(false);

const store = useMainStore();
const { orderAdmins } = storeToRefs(store);

const isOpen = ref(true);
const selectAllItems = ref(false);
const itemSelections = reactive<
  Record<string, { selectAll: boolean; selectedProducts: (string | number)[] }>
>({});

const selectedOrderAdmin = ref<OrderAdminUser | null>(null);
/** Failed thumb loads → empty slot (no `/img/placeholder.png`). */
const copyOrderFpThumbFailed = reactive<Record<string, boolean>>({});
function copyOrderThumbKey(
  itemId: string | number,
  fpId: string | number,
  side: 'f' | 'b'
): string {
  return `${itemId}-${fpId}-${side}`;
}

const emit = defineEmits(['close', 'save']);

const originalAdmin = computed<OrderAdminUser | null>(() => {
  const id = props.order?.order_administrator_id;
  if (id === null || id === undefined || id === '') return null;
  return orderAdmins.value.find((a) => String(a.id) === String(id)) ?? null;
});

const isOriginalAdminActive = computed(() => originalAdmin.value !== null);

const isSaveDisabled = computed(
  () => isSubmitting.value || (!isOriginalAdminActive.value && !selectedOrderAdmin.value)
);

onMounted(async () => {
  if (!orderAdmins.value || orderAdmins.value.length === 0) {
    await store.getOrderAdmins(ORDER_ADMIN_ROLES);
  }
});

/* -------------------------------------------------------------------------- */
/* AB#21667 (Zendesk #9310) — remake or reorder                                */
/* -------------------------------------------------------------------------- */

/**
 * The two remake symbols, mirroring App\Enums\OrderTypeSymbol::isRemake(). The trailing "i"
 * means invoiced, so both are remakes and both get asked.
 *
 * Anything else — including an order whose symbol predates the catalogue, which the server
 * reads as null — takes no prompt. The API refuses a classification sent for a non-remake,
 * so a dialog that asked more often than the server allows would be a 422 on save.
 */
const REMAKE_SYMBOLS = ['R', 'Ri'];
const sourceIsRemake = computed(() => REMAKE_SYMBOLS.includes(props.order.order_type_symbol ?? ''));

const classification = ref<'remake' | 'reorder' | null>(null);
const failureRecordAnswers = ref<Record<string, unknown> | null>(null);
const showQuestionnaire = ref(false);

// Answers describe one kind of copy. Switching away from Remake must not smuggle a failure
// record onto an order that fails nothing — the same clearing ChangeOrderTypeModal does.
watch(classification, () => {
  failureRecordAnswers.value = null;
});

function onQuestionnaireSubmit(answers: Record<string, unknown>): void {
  failureRecordAnswers.value = answers;
  showQuestionnaire.value = false;
}

/**
 * Whether the copy can be sent yet.
 *
 * No default for the remake case, deliberately: a missing answer used to mean "remake", and
 * that silence is the whole defect. A reorder needs no questionnaire because nothing failed.
 */
const canSubmit = computed(() => {
  if (sizeReports.value.length && !allSizesResolved(sizeReports.value, sizeChoices.value)) {
    return false;
  }
  if (!sourceIsRemake.value) return true;
  if (!classification.value) return false;

  return classification.value !== 'remake' || failureRecordAnswers.value !== null;
});

/* -------------------------------------------------------------------------- */
/* AB#22228 (Zendesk #9545) — sizes that no longer exist                       */
/* -------------------------------------------------------------------------- */
/**
 * Checked on Save rather than on every click, against exactly the selection being sent. The
 * check is remembered by that selection, so changing the products asks again.
 */
const sizeReports = ref<ObsoleteSizeReport[]>([]);
const sizeChoices = ref<SizeReplacementChoices>({});
const sizeCheckedFor = ref<string | null>(null);

watch(
  [itemSelections, selectAllItems],
  () => {
    sizeCheckedFor.value = null;
    sizeReports.value = [];
    sizeChoices.value = {};
  },
  { deep: true }
);

/**
 * Ask the API (a `size_check` dry run of the copy) which selected products carry obsolete sizes. Returns true when the copy may go
 * ahead now. A failed check does not block: the copy endpoint applies the same rule and answers
 * 422 with the same report, which is handled on submit.
 */
async function ensureSizesChecked(payload: Record<string, unknown>): Promise<boolean> {
  const selectionKey = JSON.stringify(payload.order_item_factory_products_map ?? null);
  if (sizeCheckedFor.value === selectionKey) return true;
  try {
    const resp = await http.post(`/copy-order/${props.order.id}`, {
      size_check: true,
      ...(payload.order_item_factory_products_map
        ? { order_item_factory_products_map: payload.order_item_factory_products_map }
        : {}),
    });
    sizeReports.value = reportsFromResponse(resp?.data);
  } catch {
    sizeReports.value = [];
  }
  sizeCheckedFor.value = selectionKey;
  return sizeReports.value.length === 0;
}

// ✅ Initialize selections before render
if (props.order?.items?.length) {
  props.order.items.forEach((item) => {
    itemSelections[item.id] = {
      selectAll: false,
      selectedProducts: [],
    };
  });
}

/* -------------------------------------------------------------------------- */
/* ✅ FIX: Explicit "Select All Items" toggle instead of watcher               */
/* -------------------------------------------------------------------------- */
const toggleSelectAllItems = () => {
  const newVal = selectAllItems.value;
  props.order.items.forEach((item) => {
    const itemSel = itemSelections[item.id];
    if (!itemSel) return;
    itemSel.selectAll = newVal;
    itemSel.selectedProducts = newVal ? item.factory_products.map((fp) => fp.id) : [];
  });
};

/* -------------------------------------------------------------------------- */
const toggleItemSelectAll = (item: OrderDetailItem) => {
  const itemSel = itemSelections[item.id];
  if (!itemSel) return;
  itemSel.selectedProducts = itemSel.selectAll ? item.factory_products.map((fp) => fp.id) : [];

  // 🧠 Sync global "Select All Items" if needed
  updateGlobalSelectAll();
};

const toggleProductSelection = (itemId: string | number, productId: string | number) => {
  const itemSel = itemSelections[itemId];
  if (!itemSel) return;
  const index = itemSel.selectedProducts.indexOf(productId);
  if (index === -1) {
    itemSel.selectedProducts.push(productId);
  } else {
    itemSel.selectedProducts.splice(index, 1);
  }

  // auto toggle "select all" for that item
  const total = props.order.items.find((i) => i.id === itemId)?.factory_products.length || 0;
  itemSel.selectAll = itemSel.selectedProducts.length === total;

  // 🧠 Sync global "Select All Items"
  updateGlobalSelectAll();
};

// 🧩 Keep top-level selectAllItems reactive (without triggering resets)
const updateGlobalSelectAll = () => {
  const allSelected = props.order.items.every((item) => itemSelections[item.id]?.selectAll);
  selectAllItems.value = allSelected;
};

/* -------------------------------------------------------------------------- */
const resolveAdminIdToAssign = (): string | number | null => {
  if (isOriginalAdminActive.value && originalAdmin.value) {
    return originalAdmin.value.id;
  }
  return selectedOrderAdmin.value?.id ?? null;
};

const buildPayload = () => {
  const adminId = resolveAdminIdToAssign();
  const payload: Record<string, unknown> = {};
  if (adminId !== null && adminId !== undefined) {
    payload.order_administrator_id = adminId;
  }

  if (!selectAllItems.value) {
    const map: Record<string, (string | number)[]> = {};
    for (const item of props.order.items) {
      const selected = itemSelections[item.id]?.selectedProducts ?? [];
      if (selected.length > 0) {
        map[item.id] = selected;
      }
    }
    payload.order_item_factory_products_map = map;
  }

  // Only ever for a remake. The API validates `classification` as prohibited on any other
  // order, so sending a stray one would turn an ordinary copy into a 422.
  if (sourceIsRemake.value && classification.value) {
    payload.classification = classification.value;

    // In the same request as the copy, as the order-type change already does, so neither can
    // land without the other: a copy classified as a remake is never created with nothing to
    // explain it.
    if (classification.value === 'remake' && failureRecordAnswers.value) {
      payload.failure_record = failureRecordAnswers.value;
    }
  }

  return payload;
};

const extractNewOrderId = (resp: any): number | string | null => {
  const data = resp?.data;
  const result = data?.result;
  if (Array.isArray(result) && result.length >= 2 && result[1]?.id != null) {
    return result[1].id;
  }
  return (
    result?.order?.id ??
    result?.id ??
    data?.order?.id ??
    data?.new_order_id ??
    data?.id ??
    null
  );
};

/**
 * Builds the warning message for a copied order that ended up with no
 * Order Admin. Returns `null` when the backend response doesn't indicate a
 * missing-admin condition (either `assigned: true` or a legacy response
 * without the `order_admin_assignment` key at all).
 */
function buildNoOrderAdminWarning(assignment: OrderAdminAssignment | undefined): string | null {
  if (!assignment || assignment.assigned) return null;

  let body: string;
  if (assignment.alert_reason === 'original_admin_inactive') {
    body =
      "The copied order was created without an Order Admin because the original order's admin is no longer active.";
  } else if (assignment.alert_reason === 'original_unassigned') {
    body =
      'The copied order was created without an Order Admin because the original order had none assigned.';
  } else {
    body = 'The copied order was created without an Order Admin.';
  }

  if (assignment.alert_sent) {
    body += ' An email alert has been sent to the configured recipients.';
  }

  return body;
}

const submitCopyOrder = async () => {
  if (!isOriginalAdminActive.value && !selectedOrderAdmin.value) {
    showToast('Please assign an Order Admin before copying.', 'error');
    return;
  }

  // The save button is disabled for the same condition, but the guard lives here too: the
  // Dialog raises saveEvent, and a disabled attribute is a hint, not a contract.
  if (!canSubmit.value || isSubmitting.value) return;

  isSubmitting.value = true;
  const payload = buildPayload();
  const adminIdToAssign = resolveAdminIdToAssign();

  // AB#22228: stop here (keeping the dialog open) when sizes need choosing first.
  if (!(await ensureSizesChecked(payload))) {
    isSubmitting.value = false;
    return;
  }
  if (sizeReports.value.length) {
    payload.size_replacements = buildSizeReplacements(sizeReports.value, sizeChoices.value);
  }

  try {
    const resp = await http.post<CopyOrderResponseData>(
      `/copy-order/${props.order.id}`,
      Object.keys(payload).length ? payload : {}
    );

    const newOrderId = extractNewOrderId(resp);
    if (adminIdToAssign !== null && newOrderId !== null) {
      try {
        await store.updateOrderAdmin({ user_id: adminIdToAssign, order_ids: newOrderId });
      } catch {
        // updateOrderAdmin surfaces its own toast on failure
      }
    }

    showToast(resp.data.message || 'Order copied successfully!', 'success');
    emit('save', true);
    closeModal();

    const warningMessage = buildNoOrderAdminWarning(resp.data.result?.order_admin_assignment);
    if (warningMessage) {
      showAlert('', warningMessage, 'warning');
    }
  } catch (err: any) {
    // AB#22228: the size list changed between the check and the copy — show the picker for
    // what the server reported instead of a generic failure.
    const obsoleteReports = reportsFromObsoleteSizeError(err);
    if (obsoleteReports && obsoleteReports.length) {
      sizeReports.value = obsoleteReports;
      sizeChoices.value = {};
      isSubmitting.value = false;
      return;
    }
    handleResponseException(err);
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit('close');
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
